import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Wallet, Calendar, User } from 'lucide-react';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const businessId = localStorage.getItem('currentBusiness');

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await api.get(`/api/payments/${businessId}`);
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentModeColor = (mode) => {
    const colors = {
      CASH: 'bg-green-100 text-green-700',
      UPI: 'bg-blue-100 text-blue-700',
      BANK_TRANSFER: 'bg-purple-100 text-purple-700',
      CARD: 'bg-orange-100 text-orange-700',
      CHEQUE: 'bg-gray-100 text-gray-700'
    };
    return colors[mode] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
          <Wallet className="w-8 h-8" />
          Payments
        </h1>
        <p className="text-sm text-gray-600 mt-1">View all payment records and transactions</p>
      </div>

      {payments.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {payments.map((payment) => (
            <Card key={payment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getPaymentModeColor(payment.mode)}>
                        {payment.mode.replace('_', ' ')}
                      </Badge>
                      <span className="text-2xl font-bold text-green-600">
                        ₹{payment.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      {payment.customer && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span>{payment.customer.name}</span>
                        </div>
                      )}
                      {payment.invoice && (
                        <div>
                          <span className="font-medium">Invoice:</span> {payment.invoice.invoiceNo}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(payment.date).toLocaleDateString('en-IN')}</span>
                      </div>
                      {payment.notes && (
                        <div className="text-xs text-gray-500 mt-2">
                          Note: {payment.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Payment Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 text-gray-500">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No payment records yet.</p>
              <p className="text-sm mt-2">Payments will appear here when you record them against invoices.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Payments;